/**
 * Access of Phage.Energy to the client's station in DeyeCloud (D3, prototype
 * v14): the client opens the station to our business account by hand
 * («Manage Access»), «Перевірити доступ» looks for it in the business
 * account's list — at most once a minute per object.
 *   `PhageAccessHowTo` — the 5 steps of «Додати об'єкт» → «Доступ» with the
 *     business name and «Скопіювати»;
 *   `PhageAccessMore` — «Як відкрити або закрити доступ у DeyeCloud» of
 *     «Налаштування»;
 *   `useAccessCheck` — «Перевірити доступ» and its result line;
 *   `PhageAccessChip` — the chip of the object screen with the check (open access);
 *   `AccessNote` — D4: the note above the object's sections while there is
 *     no access or no right to manage, with the check;
 *   `PhageAccessRow` — the row of «Налаштування».
 * D4: data and control go through this access.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import type { PhageAccessStatus, SiteResponse } from 'shared';

import { fetchSite, useCheckPhageAccess, usePhageAccessInfo } from '../hooks/use-sites.js';
import { useLocale } from '../i18n/locale-context.js';
import type { MessageKey } from '../i18n/messages.js';
import type { ApiError } from '../lib/api-client.js';
import { useNow } from './charts/day-axis.js';
import { apiCode, errorText } from './errors.js';
import { fmtDateTime, fmtTime } from './format.js';

/** Chip value of a status: «очікує доступу», «доступ відкрито» … */
export const accessStatusKey = (status: PhageAccessStatus): MessageKey => `access.status.${status}`;

/** The business name with «Скопіювати»; no clipboard — the name is selected to copy by hand. */
function CopyName({ name }: { name: string }) {
  const { t } = useLocale();
  const [state, setState] = useState<'idle' | 'copied' | 'selected'>('idle');
  const text = useRef<HTMLElement>(null);
  const copy = () => {
    const select = () => {
      const el = text.current;
      const sel = window.getSelection();
      if (el && sel) sel.selectAllChildren(el);
      setState('selected');
    };
    try {
      navigator.clipboard.writeText(name).then(() => setState('copied'), select);
    } catch {
      select();
    }
  };
  return (
    <span className="copy-name">
      <b ref={text}>{name}</b>
      <button type="button" className="btn small" onClick={copy}>
        {t(state === 'copied' ? 'access.copied' : 'access.copy')}
      </button>
      {state === 'selected' ? (
        <span className="sub" aria-live="polite">
          {t('access.copy_selected')}
        </span>
      ) : null}
    </span>
  );
}

/** «Додати об'єкт» → «Доступ»: what to do in DeyeCloud, the business name to copy (null — not known). */
export function PhageAccessHowTo({ name }: { name: string | null }) {
  const { t } = useLocale();
  return (
    <ol className="howto">
      <li>{t('access.howto.1')}</li>
      <li>{t('access.howto.2')}</li>
      {name ? (
        <li>
          {t('access.howto.3')} <CopyName name={name} />
        </li>
      ) : (
        <li>{t('access.howto.3_unknown')}</li>
      )}
      <li>{t('access.howto.4')}</li>
      <li>{t('access.howto.5')}</li>
    </ol>
  );
}

/** «Налаштування»: how to open or close the access, folded. */
export function PhageAccessMore({ name }: { name: string | null }) {
  const { t, tf } = useLocale();
  const shown = name ?? 'Phage.Energy';
  return (
    <details className="more" style={{ marginTop: 12 }}>
      <summary>{t('access.more')}</summary>
      <ol className="howto" style={{ marginTop: 10 }}>
        <li>{t('access.more.1')}</li>
        <li>
          {tf('access.more.2', { name: shown })}
          {name ? (
            <>
              {' '}
              <CopyName name={name} />
            </>
          ) : null}
        </li>
        <li>{tf('access.more.3', { name: shown })}</li>
      </ol>
    </details>
  );
}

export interface AccessCheck {
  check: () => void;
  /** The button is off: a check runs, or the next one is not due yet. */
  disabled: boolean;
  /** The result of the last check of this screen, or why there is none; when the next one is possible. */
  message: string | null;
  /** The message reports a problem: not seen, closed, no check possible. */
  bad: boolean;
}

/**
 * «Перевірити доступ» of an object: the result as a line — open, «Ще не
 * бачимо станцію…», «перевірка недоступна», DeyeCloud's sign-in limit, the
 * time the next check is possible. `onSite` gets the stored object.
 */
export function useAccessCheck(site: SiteResponse, onSite?: (site: SiteResponse) => void): AccessCheck {
  const { t, tf } = useLocale();
  const qc = useQueryClient();
  const check = useCheckPhageAccess(site.id);
  const nowMs = useNow(5_000);
  const tz = site.timezone;
  const code = apiCode(check.error);
  const retryAt = (check.error as ApiError | null)?.details?.retry_at;
  // 429 `too_soon` carries the time the next check is possible; `login_limited` — when
  // DeyeCloud lets the business account sign in again (a check before that can't work).
  const waitFor = (code === 'too_soon' || code === 'login_limited') && typeof retryAt === 'string';
  const nextAt = waitFor ? retryAt : site.phage_access.next_check_at;
  const next = nextAt && Date.parse(nextAt) > nowMs ? nextAt : null;
  const result = check.data?.phage_access.status;
  let message: string | null = null;
  if (check.isSuccess && result) message = t(`access.result.${result}`);
  else if (code === 'check_unavailable') message = t('access.unavailable');
  else if (code === 'login_limited' && typeof retryAt === 'string')
    message = tf('access.limited', { time: fmtTime(retryAt, tz) });
  else if (check.error && code !== 'too_soon') message = errorText(check.error, tf);
  const bad = message !== null && !(check.isSuccess && result === 'open');
  // The button is off until then: say when (an open access needs no new check; the
  // sign-in limit already says it).
  if (next && site.phage_access.status !== 'open' && code !== 'login_limited') {
    message = [message, tf('access.next', { time: fmtTime(next, tz) })].filter(Boolean).join(' ');
  }
  return {
    check: () =>
      check.mutate(undefined, {
        onSuccess: (s) => onSite?.(s),
        // A check without an answer still used the object's minute: the stored attempt comes back.
        onError: () => {
          if (!onSite) return;
          qc.fetchQuery({
            queryKey: ['site', site.id],
            queryFn: () => fetchSite(site.id),
            staleTime: 0,
          }).then(onSite, () => {});
        },
      }),
    disabled: check.isPending || !!next,
    message: check.isPending ? null : message,
    bad,
  };
}

/** The object screen: «Phage.Energy: очікує доступу» and «Перевірити доступ»; open — hidden on a phone. */
export function PhageAccessChip({ site }: { site: SiteResponse }) {
  const { t } = useLocale();
  const c = useAccessCheck(site);
  const status = site.phage_access.status;
  const open = status === 'open';
  return (
    <span className={`access-check${open ? ' chip-minor' : ''}`}>
      <span className={`chip${open ? '' : ' warn'}`}>
        <span className="k">Phage.Energy</span>
        {t(accessStatusKey(status))}
      </span>
      <button type="button" className="btn small" disabled={c.disabled} onClick={c.check}>
        {t('access.check')}
      </button>
      {c.message ? (
        <span className={c.bad ? 'sub err' : 'sub'} aria-live="polite">
          {c.message}
        </span>
      ) : null}
    </span>
  );
}

/** «Налаштування» → «Доступ Phage.Energy»: the status, when checked, «Перевірити доступ». */
export function PhageAccessRow({ site }: { site: SiteResponse }) {
  const { t, tf } = useLocale();
  const c = useAccessCheck(site);
  const a = site.phage_access;
  const text = [
    t(`access.row.${a.status}`),
    a.checked_at ? tf('access.checked_at', { at: fmtDateTime(a.checked_at, site.timezone) }) : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <>
      {text}
      {' · '}
      <button type="button" className="linkbtn" disabled={c.disabled} onClick={c.check}>
        {t('access.check')}
      </button>
      {c.message ? (
        <span
          className={c.bad ? 'sub err' : 'sub'}
          style={{ display: 'block', marginTop: 4 }}
          aria-live="polite"
        >
          {c.message}
        </span>
      ) : null}
    </>
  );
}

/**
 * D4, the object screen above the sections (prototype v14): «Очікує доступу»
 * and «Доступ закрито» — no data, no control, how to open the access and
 * «Перевірити доступ»; «Немає права керування» — data comes, which rights to
 * give, then switch internal arbitrage on again (a check would not change it).
 */
export function AccessNote({ site, state }: { site: SiteResponse; state: 'pending' | 'closed' | 'noedit' }) {
  const { t, tf } = useLocale();
  const info = usePhageAccessInfo();
  const c = useAccessCheck(site);
  const name = info.data?.company_name ?? 'Phage.Energy';
  return (
    <div className={`hist-note ${state === 'closed' ? 'err' : 'warn'}`} aria-live="polite">
      <span>
        {tf(`site.connnote.${state}`, { name })}
        {state !== 'noedit' && c.message ? (
          <span className={c.bad ? 'sub err' : 'sub'} style={{ display: 'block', marginTop: 4 }}>
            {c.message}
          </span>
        ) : null}
      </span>
      {state === 'noedit' ? null : (
        <button type="button" className="btn" disabled={c.disabled} onClick={c.check}>
          {t('access.check')}
        </button>
      )}
    </div>
  );
}
